const API_BASE = '';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      res.ok
        ? 'Unexpected response from server'
        : res.statusText || 'Request failed',
    );
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || res.statusText) as Error & { status?: number; data?: unknown };
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as T;
}

function qs(params: Record<string, string | undefined>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  authGoogle: (credential: string) =>
    request<{ user: User }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request<{ user: User }>('/users/me'),
  listDashboards: () => request<{ dashboards: Dashboard[] }>('/dashboards/'),
  listShared: () => request<{ dashboards: Dashboard[] }>('/dashboards/shared'),
  browseDashboards: (params: BrowseParams = {}) =>
    request<{ items: BrowseDashboardItem[]; lastKey?: string | null }>(
      `/dashboards/browse${qs({
        q: params.q,
        game: params.game,
        tags: params.tags?.join(','),
        category: params.category,
        uploadType: params.uploadType,
        createdBy: params.createdBy,
        sort: params.sort,
        limit: params.limit?.toString(),
        lastKey: params.lastKey,
      })}`,
    ),
  getDashboard: (id: string) => request<{ dashboard: Dashboard }>(`/dashboards/${id}`),
  listTags: () => request<{ tags: string[] }>('/dashboards/tags'),
  listAssetArchive: () =>
    request<{ prefixes: string[]; total: number }>('/dashboards/asset-archive'),
  assetArchiveDetails: (prefixes: string[]) =>
    request<{ folders: AssetFolder[] }>('/dashboards/asset-archive/details', {
      method: 'POST',
      body: JSON.stringify({ prefixes }),
    }),
  downloadAssetFolder: (prefix: string) =>
    request<{ url: string; filename: string }>('/dashboards/asset-archive/download', {
      method: 'POST',
      body: JSON.stringify({ prefix }),
    }),
  adminListTags: () => request<{ tags: string[] }>('/admin/tags'),
  adminAddTag: (tag: string) =>
    request<{ tags: string[] }>('/admin/tags', { method: 'POST', body: JSON.stringify({ tag }) }),
  adminRemoveTag: (tag: string) =>
    request<{ tags: string[] }>(`/admin/tags/${encodeURIComponent(tag)}`, { method: 'DELETE' }),
  adminListLoginDomains: () => request<{ domains: string[] }>('/admin/login-domains'),
  adminAddLoginDomain: (domain: string) =>
    request<{ domains: string[] }>('/admin/login-domains', { method: 'POST', body: JSON.stringify({ domain }) }),
  adminRemoveLoginDomain: (domain: string) =>
    request<{ domains: string[] }>(`/admin/login-domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
  updateDashboardMeta: (id: string, body: { title?: string; tags?: string[]; game?: string; visibility?: string }) =>
    request<{ dashboard: Dashboard }>(`/dashboards/${id}/meta`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getScreenshotUploadUrl: (id: string, contentType: string) =>
    request<{ presignedUrl: string; screenshotKey: string }>(`/dashboards/${id}/screenshot-upload-url`, {
      method: 'POST',
      body: JSON.stringify({ contentType }),
    }),
  confirmScreenshot: (id: string, screenshotKey: string) =>
    request<{ screenshotUrl: string }>(`/dashboards/${id}/screenshot-confirm`, {
      method: 'POST',
      body: JSON.stringify({ screenshotKey }),
    }),
  listComments: (id: string, params?: { limit?: number; lastKey?: string; sort?: 'newest' | 'oldest' }) =>
    request<{ comments: Comment[]; nextKey: string | null }>(
      `/dashboards/${id}/comments${qs({ limit: params?.limit?.toString(), lastKey: params?.lastKey, sort: params?.sort })}`,
    ),
  postComment: (id: string, text: string) =>
    request<{ comment: Comment }>(`/dashboards/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  rateCanvas: (id: string, rating: number) =>
    request<{ rating: number; ratingAverage: number | null; ratingCount: number }>(`/dashboards/${id}/rating`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    }),
  getMyRating: (id: string) =>
    request<{ rating: number | null }>(`/dashboards/${id}/rating/me`),
  deleteDashboard: (id: string) =>
    request(`/dashboards/${id}`, { method: 'DELETE' }),
  getVersions: (id: string) =>
    request<{ versions: DashboardVersion[] }>(`/dashboards/${id}/versions`),
  getViewUrl: (id: string, version?: number) =>
    request<ViewUrlResponse>(
      `/dashboards/${id}/view-url${version ? `?version=${version}` : ''}`,
    ),
  createSignedUrl: (id: string, body: { ttlSeconds: number }) =>
    request<SignedUrlRecord>(`/dashboards/${id}/signed-urls`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listSignedUrls: (id: string) =>
    request<{ signedUrls: SignedUrlRecord[] }>(`/dashboards/${id}/signed-urls`),
  listShares: (id: string) => request<{ shares: ShareUser[] }>(`/dashboards/${id}/shares`),
  shareDashboard: (id: string, email: string) =>
    request(`/dashboards/${id}/shares`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  unshareDashboard: (id: string, userId: string) =>
    request(`/dashboards/${id}/shares/${userId}`, { method: 'DELETE' }),
  listUsers: () => request<{ users: User[] }>('/users/'),
  createUser: (body: Partial<User> & { email: string }) =>
    request('/users/', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (userId: string, body: Partial<User>) =>
    request(`/users/${userId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteUser: (userId: string) =>
    request(`/users/${userId}`, { method: 'DELETE' }),
  adminHealth: () => request<{ status: string }>('/admin/health'),
  requestUpload: (body: { title?: string; visibility?: string; filename: string; tags?: string[]; category?: 'playable' | 'html'; canvas_type?: 'prototype' | 'dashboard' }) =>
    request<{ dashboardId: string; presignedUrl: string; uploadType: string }>('/dashboards/upload-request', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  completeUpload: (dashboardId: string) =>
    request<{ dashboardId: string; version: number }>(`/dashboards/${dashboardId}/upload-complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  requestUploadNewVersion: (id: string) =>
    request<{ presignedUrl: string }>(`/dashboards/${id}/upload-new-version-request`, { method: 'POST', body: JSON.stringify({}) }),
  completeUploadNewVersion: (id: string) =>
    request<{ version: number }>(`/dashboards/${id}/upload-new-version-complete`, { method: 'POST', body: JSON.stringify({}) }),
};

export interface AssetFolder {
  prefix: string;
  name: string;
  fileCount: number;
  totalBytes: number;
  formats: string[];
  thumbnailUrl: string | null;
}

export interface User {
  userId: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin' | 'external';
  enabled?: boolean;
  createdAt?: string;
  lastLoginAt?: string | null;
}

export interface Comment {
  commentId: string;
  dashboardId: string;
  userId: string;
  authorName: string;
  text: string;
  createdAt: string;
}

export interface Dashboard {
  dashboardId: string;
  userId: string;
  title: string;
  description?: string;
  status: string;
  currentVersion?: number;
  tags?: string[];
  game?: string;
  category?: 'playable' | 'html' | 'report';
  visibility?: 'private' | 'discoverable';
  dataLastRefreshedAt?: string;
  createdAt: string;
  updatedAt: string;
  generationPrompt?: string;
  uploadType?: 'prototype' | 'dashboard' | 'generated' | 'html' | 'zip' | null;
  screenshotUrl?: string | null;
  ratingAverage?: number | null;
  ratingCount?: number;
  createdByName?: string | null;
}

export interface BrowseDashboardItem {
  dashboardId: string;
  title: string;
  description?: string;
  tags?: string[];
  game?: string;
  category?: 'playable' | 'html' | 'report';
  createdByName?: string;
  createdByUserId?: string;
  updatedAt: string;
  screenshotUrl?: string | null;
  ratingAverage?: number | null;
  ratingCount?: number;
}

export interface BrowseParams {
  q?: string;
  game?: string;
  tags?: string[];
  category?: 'playable' | 'html' | 'report';
  uploadType?: 'prototype' | 'dashboard';
  createdBy?: string;
  sort?: 'updated' | 'title' | 'rating';
  limit?: number;
  lastKey?: string;
}

export interface DashboardVersion {
  SK: string;
  s3Key: string;
  generationPrompt: string;
  createdAt: string;
  queryType?: string;
}

export interface ViewUrlResponse {
  url: string;
  htmlUrl?: string;
  dataUrl?: string;
  version: number;
  expiresAt?: string;
}

export interface SignedUrlRecord {
  urlId: string;
  url: string;
  createdAt?: string;
  expiresAt: string | null; // null = never expires
}


export interface ShareUser {
  userId: string;
  email: string;
  displayName: string;
}







export const SIGNED_URL_TTL_OPTIONS = [
  { label: 'Never', seconds: 0 },
  { label: '1 day', seconds: 1 * 86400 },
  { label: '2 days', seconds: 2 * 86400 },
  { label: '3 days', seconds: 3 * 86400 },
  { label: '4 days', seconds: 4 * 86400 },
  { label: '5 days', seconds: 5 * 86400 },
  { label: '6 days', seconds: 6 * 86400 },
  { label: '7 days', seconds: 7 * 86400 },
] as const;
